<template>
  <UiComponentWrapper class="page">
    <!-- Section > Navigation -->
    <UiComponentContainer class="navigation">
      <UiComponentWrapper class="branding">
        <AtomLogo />
      </UiComponentWrapper>
      <UiComponentWrapper class="action">
        <AtomButton
          :action="downloadCallToAction.action"
          label="Navigation bar CTA"
          size="l"
          @click="downloadCallToAction.callback"
        >
          {{ downloadCallToAction.slot }}
        </AtomButton>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Hero -->
    <UiComponentContainer class="jumbotron">
      <OrganismHero
        alignment="left"
        :html="props.page.pageSubtitle"
        :primary-button="{
          ...downloadCallToAction,
          label: 'Header section CTA',
        }"
        :tag-with-image="{
          icon: props.page.resourceType.icon,
          color: props.page.resourceType.color,
        }"
        @primary:click="downloadCallToAction.callback"
      >
        <template #tag>
          {{ props.page.resourceType.name }}
        </template>
        <template #heading>
          {{ props.page.pageTitle }}
        </template>
      </OrganismHero>

      <UiComponentImage
        v-if="props.page.image"
        :key="`hero-image-${heroImageSize}`"
        :alt="props.page.image.alt"
        class="illustation"
        :expected-size="heroImageSize"
        :height="props.page.image.height"
        :source="props.page.image.url"
        :width="props.page.image.width"
      />
    </UiComponentContainer>

    <!-- Section > Sliders -->
    <UiComponentContainer
      v-for="({ id, images, title, text }, index) in props.page.videoSliders"
      :key="`slider-${id}`"
      class="section"
      :class="{ first: index === 0, even: index%2 === 0 }"
    >
      <UiComponentWrapper class="copy">
        <UiComponentHeading :level="2">
          {{ title }}
        </UiComponentHeading>
        <div
          class="text"
          v-html="$md.render(text)"
        />
        <AtomButton
          icon-position="icon-right"
          size="l"
          @click="downloadCallToAction.callback"
        >
          {{ downloadCallToAction.slot }}
        </AtomButton>
      </UiComponentWrapper>
      <OrganismSlider>
        <UiComponentImage
          v-for="(image, subindex) in images"
          :key="`slider-${subindex}-size-${sliderImagesSize}`"
          :alt="image.alt"
          class="slide"
          :expected-size="sliderImagesSize"
          :height="image.height"
          :source="image.url"
          :width="image.width"
        />
      </OrganismSlider>
    </UiComponentContainer>

    <!-- Section > How to -->
    <UiComponentContainer class="how-to">
      <UiComponentHeading :level="2">
        {{ props.componentHowToAddAVideo.title }}
      </UiComponentHeading>
      <UiComponentWrapper class="list">
        <UiComponentWrapper
          v-for="{ id, image, title, text } in props.componentHowToAddAVideo.steps"
          :key="`how-to-${id}`"
          class="item"
        >
          <UiComponentWrapper class="illustation">
            <UiComponentImage
              v-if="image"
              :key="`how-to-image-${id}-x-${howToImagesSize}`"
              :alt="image.alt"
              class="illustation"
              :expected-size="howToImagesSize"
              :height="image.height"
              :source="image.url"
              :width="image.width"
            />
            <UiComponentWrapper class="step">
              <UiComponentHeading
                as="paragraph"
                :level="5"
              >
                {{ title }}
              </UiComponentHeading>
            </UiComponentWrapper>
          </UiComponentWrapper>
          <UiComponentParagraph class="description">
            {{ text }}
          </UiComponentParagraph>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Download -->
    <UiComponentContainer class="download">
      <OrganismHero
        alignment="left"
        :html="props.page.descriptionFormSection"
        :level="2"
      >
        <template #heading>
          {{ props.page.titleFormSection }}
        </template>
      </OrganismHero>

      <UiComponentWrapper class="inner">
        <UiComponentWrapper
          v-if="!leadHasBeenCaptured"
          class="step one"
        >
          <OrganismHubspotNativeForm
            ref="$pageForm"
            :form-id="props.componentOfferForm.formId"
            unique-id="form-ebook"
            @hubspot:form:ready="onReady($pageForm)"
            @hubspot:form:submitted="onSubmitted"
          />
          <div
            v-if="props.page.coMarketing"
            class="gdpr"
            v-html="props.page.rgpdFormText"
          />
        </UiComponentWrapper>
        <UiComponentWrapper
          v-else
          class="step two"
        >
          <UiComponentHeading
            as="paragraph"
            :level="3"
          >
            {{ props.componentForm.offerVideoTitle }}
          </UiComponentHeading>
          <UiComponentParagraph class="paragraph">
            {{
              props.componentForm.offerSuccessText
            }}
          </UiComponentParagraph>
          <AtomButton
            icon-position="icon-right"
            size="l"
            @click="downloadCallToAction.callback"
          >
            <UiComponentIcon type="download" />
            {{ downloadCallToAction.slot }}
          </AtomButton>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Discover -->
    <UiComponentContainer class="discover">
      <UiComponentHeading :level="2">
        {{ props.page.relatedVirtualBackgroundsTitle }}
      </UiComponentHeading>
      <UiComponentWrapper class="list">
        <MoleculeVirtualBackgroundCard
          v-for="{
            id,
            category,
            image,
            isDaylightBackground,
            slug,
            title,
          } in virtualBackgrounds"
          :key="id"
          as="paragraph"
          class="item"
          :image="image"
          :is-daylight="isDaylightBackground"
          :to="
            localePath({
              name: 'virtual-background-library-slug',
              params: {
                slug,
              },
            })
          "
        >
          <template #tag>
            {{ category.name }}
          </template>
          <template #paragraph>
            {{ title }}
          </template>
        </MoleculeVirtualBackgroundCard>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Get Started -->
    <UiComponentContainer class="get-started">
      <OrganismCallToAction
        :features="livestormFeatures"
        :primary-button="{
          ...requestDemoCallToAction,
          label: 'Big section CTA',
        }"
        :secondary-button="{
          ...signupOrUpgradeCallToAction,
          label: 'Big section CTA',
        }"
      >
        <template #heading>
          {{ props.componentGetStarted.title }}
        </template>
        <template #paragraph>
          {{ props.componentGetStarted.paragraph }}
        </template>
      </OrganismCallToAction>
    </UiComponentContainer>

    <!-- Modal > Form -->
    <Transition
      @enter="onEnter"
      @leave="onLeave"
    >
      <UiComponentModal
        v-if="modalIsDisplayed && !leadHasBeenCaptured"
        @modal:close="closeModal"
      >
        <template #content>
          <UiComponentWrapper class="form">
            <OrganismHubspotNativeForm
              ref="$modalForm"
              :form-id="props.componentOfferForm.formId"
              unique-id="form-ebook-modal"
              @hubspot:form:ready="onReady($modalForm)"
              @hubspot:form:submitted="onSubmitted"
            />
          </UiComponentWrapper>
        </template>
      </UiComponentModal>
    </Transition>
  </UiComponentWrapper>
</template>

<script setup>
import { gsap } from 'gsap'

// Props
const props = defineProps({
  componentForm: {
    required: true,
    type: Object,
  },
  componentGetStarted: {
    required: true,
    type: Object,
  },
  componentGlobal: {
    required: true,
    type: Object,
  },
  componentHowToAddAVideo: {
    required: true,
    type: Object,
  },
  componentOfferForm: {
    required: true,
    type: Object,
  },
  componentVirtualBackgrounds: {
    required: true,
    type: Object,
  },
  hub: {
    required: true,
    type: Object,
  },
  page: {
    required: true,
    type: Object,
  },
})

// Composables
const { page, track } = useAnalyticsJS()
const { locale } = useI18n()
const { isLogged } = useIsLoggedIn()
const localePath = useLocalePath()
const { $md } = useMarkdown()
const { $slugify } = useNuxtApp()
const { public: publicRuntimeConfig } = useRuntimeConfig()
const { width: screenWidth } = useScreenSize()

// Refs
const $modalForm = ref(null)
const $pageForm = ref(null)
const leadHasBeenCaptured = ref(false)
const modalIsDisplayed = ref(false)

// Computed Properties
const downloadCallToAction = computed(() => {
  return {
    action: 'Offer clicked',
    callback: () => {
      leadHasBeenCaptured.value ? downloadFile() : openModal()
    },
    slot: props.hub.ctaDownload,
    to: '#',
  }
})

const heroImageSize = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 32
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return Math.min(screenWidth.value - 48, 620)
    default:
      return 620
  }
})

const howToImagesSize = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 32
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return Math.min(screenWidth.value - 48, 530)
    default:
      return 400
  }
})

const livestormFeatures = computed(() => {
  return [props.componentGlobal.noCreditCard, props.componentGlobal.noInstall]
})

const requestDemoCallToAction = computed(() => {
  return {
    action: 'Demo clicked',
    slot: props.componentGlobal.ctaGetDemo,
    to: '#form-demo',
  }
})

const signupOrUpgradeCallToAction = computed(() => {
  return isLogged.value
    ? {
        action: 'Upgrade clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/settings?page=settings&tab=billing`,
        slot: props.componentGlobal.ctaUpgradeNow,
      }
    : {
        action: 'Signup clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/signup`,
        slot: props.componentGlobal.ctaTryForFreeShort,
      }
})

const sliderImagesSize = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 32
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return Math.min(screenWidth.value - 48, 720)
    default:
      return 510
  }
})

const trackName = computed(() => {
  return props.page.trackName || props.page.trackNameFallback
})

const virtualBackgrounds = computed(() => {
  return [
    ...props.page.relatedVirtualBackgrounds,
    ...props.componentVirtualBackgrounds,
  ]
    .filter(({ slug }) => !!slug)
    .map(
      ({
        isDaylightBackground,
        ...props
      }) => ({
        ...props,
        isDaylightBackground: !!isDaylightBackground,
      }),
    )
    .slice(0, 4)
})

// Methods
function closeModal() {
  modalIsDisplayed.value = false
}

async function downloadFile() {
  const file = await fetch(props.page.videoFile.url)
  const blob = await file.blob()
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = $slugify(trackName.value)
  anchor.click()
}

function onEnter($el, done) {
  gsap.from(
    $el.querySelector('.background'),
    {
      autoAlpha: 0,
      duration: 0.6,
      ease: 'expo.out',
    },
  )
  gsap.from(
    $el.querySelector('.box'),
    {
      autoAlpha: 0,
      delay: 0.15,
      duration: 0.6,
      ease: 'expo.out',
      onComplete: () => {
        done()
      },
      scale: 0.9,
      y: 80,
    },
  )
}

function onLeave($el, done) {
  gsap.to(
    $el.querySelector('.box'),
    {
      autoAlpha: 0,
      duration: 0.3,
      ease: 'expo.in',
      scale: 0.9,
      y: 80,
    },
  )
  gsap.to(
    $el.querySelector('.background'),
    {
      autoAlpha: 0,
      delay: 0.075,
      duration: 0.3,
      ease: 'expo.in',
      onComplete: () => {
        done()
      },
    },
  )
}

function onReady($ref) {
  nextTick(() => {
    $ref.setHiddenInputValueFor(
      'ebook_name',
      props.page.title,
    )
  })
}

async function onSubmitted(submittedValues) {
  // Trigger event to Google Tag Manager
  if (window.dataLayer && props.componentOfferForm.gtmEvent) {
    const { v4: uuidv4 } = await import('uuid')
    dataLayer.push({ event: `${props.componentOfferForm.gtmEvent}.${uuidv4()}` }) // Send event to GTM
  }

  // Trigger event to Segment
  const properties = props.componentOfferForm.segmentWhitelistedProperties
    ? props.componentOfferForm.segmentWhitelistedProperties
        .split(',')
        .map(value => value.trim())
        .reduce((accumulator, value) => {
          return {
            ...accumulator,
            [value]: submittedValues[value] ? submittedValues[value] : undefined,
          }
        }, {})
    : submittedValues

  await track(props.componentOfferForm.segmentEvent || 'Form submitted', {
    ...properties,
    category: 'Form',
    form_id: props.componentOfferForm.formId,
    label: props.componentOfferForm.segmentLabel || props.componentOfferForm.segmentEvent || 'Form submitted',
    locale: locale.value,
    title: window.document.title,
  })

  leadHasBeenCaptured.value = true
  downloadFile()
}

function openModal() {
  modalIsDisplayed.value = true
}

// Lifecycle Hooks
onMounted(() => {
  page(trackName.value)
})
</script>

<style lang="postcss" scoped>
.page {
  @apply flex-col items-center pb-14;

  @screen md {
    @apply pb-26;
  }

  .navigation {
    @apply items-center h-13 z-50;

    @screen xl {
      @apply h-[88px];
    }

    .branding {
      .logo {
        :deep(img) {
          @apply h-5 w-auto !important;

          @screen xl {
            @apply h-6 !important;
          }
        }
      }
    }

    .action {
      @apply hidden;

      @screen xl {
        @apply flex flex-shrink-0 w-auto;
      }
    }
  }

  .jumbotron {
    @apply flex-col items-center mt-10 mb-14;

    @screen md {
      @apply mb-16;
    }

    @screen xl {
      @apply flex-row justify-between;
    }

    .hero {
      @screen xl {
        @apply w-span6;
      }
    }

    .illustation {
      @apply mt-12 rounded-md;

      @screen md {
        @apply mt-18 rounded-xl;
      }

      @screen xl {
        @apply w-span6 mt-0;
      }
    }
  }

  .section {
    @apply flex-col mt-14;

    @screen md {
      @apply mt-26;
    }

    @screen xl {
      @apply flex-row items-center justify-between;
    }

    &.first {
      @apply mt-0;
    }

    &.even {
      .slider {
        @screen xl {
          @apply order-first;
        }
      }
    }

    .copy {
      @apply flex-col;

      @screen xl {
        @apply w-span6;
      }

      :deep(.text) {
        @apply mt-4;

        p {
          @apply text-origin text-grey-blue-600 leading-7;
        }

        ul {
          @apply flex flex-col;

          li {
            @apply flex items-start mt-4 text-grey-blue-600 text-origin leading-7;

            &::before {
              @apply flex flex-shrink-0 justify-center items-center w-4 h-4 mt-[6px] mr-2 bg-livestorm-blue-200 bg-no-repeat bg-center content-[""] rounded-full;
              background-image: url("https://livestorm.imgix.net/1127/1615557777-check.png");
              background-size: 10px 8px;
            }
          }
        }

        p + p,
        p + ul
        ul + p
        ul + ul, {
          @apply mt-4;
        }
      }

      .button {
        @apply self-start mt-6;
      }
    }

    .slider {
      @apply flex-col mt-8;

      @screen xl {
        @apply w-span5 mt-0;
      }
    }
  }

  .how-to {
    @apply flex-col items-center mt-14;

    @screen md {
      @apply mt-26;
    }

    .h2 {
      @apply text-center;
    }

    .list {
      @apply flex-col items-center mt-12;

      @screen xl {
        @apply flex-row flex-wrap justify-between;
      }

      .item {
        @apply flex-col max-w-[530px] mt-10;

        @screen xl {
          @apply mt-0;
          width: calc((100% - 80px) / 3);
        }

        &:nth-child(1) {
          @apply mt-0;
        }

        .illustation {
          @apply relative pb-16/9 bg-sunburst-red-100 border-2 border-sunburst-red-200 rounded-3xl overflow-hidden;

          .step {
            @apply absolute left-4 top-4 items-center justify-center h-10 w-10 bg-grey-blue-100 rounded;

            .h5 {
              @apply leading-none;
            }
          }
        }

        .description {
          @apply mt-6 text-grey-blue-600;
        }
      }
    }
  }

  .download {
    @apply flex-col items-center mt-14;

    @screen md {
      @apply mt-26;
    }

    @screen xl {
      @apply flex-row justify-between;
    }

    .hero {
      @screen xl {
        @apply w-span5;
      }

      :deep(.description) {
        ul {
          @apply flex flex-col;

          li {
            &:first-child {
              @apply mt-0;
            }
          }
        }
      }
    }

    .inner {
      @apply mt-8 p-6 rounded-4xl shadow-light3;

      @screen md {
        @apply mt-12 p-[50px];
      }

      @screen xl {
        @apply w-span6 mt-0 p-12;
      }

      .step {
        @apply flex-col;

        &.two {
          .h3 + p {
            @apply mt-4 text-origin text-grey-blue-600 leading-7;
          }

          .button {
            @apply mt-6;

            .icon {
              @apply mr-2 text-white;
            }
          }
        }
      }
    }
  }

  .discover {
    @apply flex-col items-center mt-14;

    @screen md {
      @apply mt-26;
    }

    .h2 {
      @apply text-center;
    }

    .list {
      @apply flex-col mt-12;

      @screen md {
        @apply flex-row flex-wrap;
      }

      .item {
        @screen md {
          width: calc((100% - 40px) / 2);
        }

        @screen xl {
          width: calc((100% - 120px) / 4);
        }

        &:nth-child(1) {
          @apply mt-0;

          @screen md {
            @apply ml-0 mr-5;
          }
        }

        &:nth-child(2) {
          @apply mt-8;

          @screen md {
            @apply ml-5 mr-0 mt-0;
          }

          @screen xl {
            @apply mx-5;
          }
        }

        &:nth-child(3) {
          @apply mt-8;

          @screen md {
            @apply ml-0 mr-5 mt-12;
          }

          @screen xl {
            @apply mx-5 mt-0;
          }
        }

        &:nth-child(4) {
          @apply mt-8;

          @screen md {
            @apply ml-5 mr-0 mt-12;
          }

          @screen xl {
            @apply mt-0;
          }
        }
      }
    }
  }

  .get-started {
    @apply mt-14;

    @screen md {
      @apply mt-26;
    }
  }

  .modal {
    @apply z-[60];

    .success {
      @apply flex-col;

      p {
        @apply text-origin text-grey-blue-600 leading-7;
      }

      .button {
        @apply mt-6;

        .icon {
          @apply mr-2 text-white;
        }
      }
    }
  }
}
</style>
